import { Injectable } from '@angular/core';
import { AnalyticsService } from '../api/analytics.service';
import { SessionIdService } from './session-id.service';
import { buildDate, commitHash } from '../../../environments/version';
import { ScreenDimensions } from '../../../api/gen';
import { StorePageVisitRequest } from '../../model/analytics/request/store-page-visit-request';
import { sentryLogger } from 'shared/core';

@Injectable()
export class PageVisitService {
  private readonly sessionKey = 'blankmileHelperClientSessionId';
  private isReporting = false;

  constructor(
    private readonly analyticsService: AnalyticsService,
    private readonly sessionIdService: SessionIdService,
  ) {}

  reportPageVisitOnce(client: string): void {
    const storageKey = `${client}PageVisitLogged`;
    if (sessionStorage.getItem(storageKey) || this.isReporting) {
      return;
    }
    this.isReporting = true;

    const sessionId = this.sessionIdService.getOrCreateSessionId(
      this.sessionKey,
    );

    const languages = Array.from(navigator.languages ?? []);
    const acceptLanguage =
      languages.length > 0 ? languages.join(',') : navigator.language;

    let timeZone: string | undefined;
    try {
      timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      timeZone = undefined;
    }

    const screenInfo: ScreenDimensions = {
      density: window.devicePixelRatio,
      pageHeight: window.innerHeight,
      pageWidth: window.innerWidth,
      screenHeight: window.screen.height,
      screenWidth: window.screen.width,
    };

    const payload: StorePageVisitRequest = {
      clientTimestamp: new Date().toISOString(),
      sessionId: sessionId,
      appName: 'helper-client',
      appVersion: commitHash,
      appBuild: buildDate,
      path: `${window.location.pathname}${window.location.search}`,
      userAgent: navigator.userAgent,
      acceptLanguage: acceptLanguage,
      language: navigator.language,
      languages: languages,
      referrer: document.referrer || undefined,
      timeZone: timeZone,
      utcOffsetMinutes: -new Date().getTimezoneOffset(),
      screenInfo: screenInfo,
    };

    this.analyticsService.storePageVisit(payload).subscribe({
      next: () => {
        sessionStorage.setItem(storageKey, 'true');
        this.isReporting = false;
      },
      error: (error) => {
        // Avoid surfacing page-visit failures as unhandled app errors; this is non-critical telemetry.
        sentryLogger.warn('Failed to store helper page visit', {
          error: error,
          path: payload.path,
        });
        this.isReporting = false;
      },
    });
  }
}
